import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import React, { ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import InfiniteScrollSelect from "../../components/ui/InfiniteScrollSelect";
import {
  BookingStatus,
  BookingStatusDisplayValue,
  BookingTypes,
  PaymentOptions,
} from "../../helpers/enums";
import { useAuth } from "../../hooks/useAuth";
import { useDebounce } from "../../hooks/useDebounce";
import { useQueryParam } from "../../hooks/useQueryParam";
import { controlStyle } from "../../helpers/constants";

const STATUS_FILTERS = [
  { label: BookingStatusDisplayValue.ALL, key: BookingStatus.ALL },
  { label: BookingStatusDisplayValue.CONFIRMED, key: BookingStatus.CONFIRMED },
  { label: BookingStatusDisplayValue.PASSED, key: BookingStatus.PASSED },
  { label: BookingStatusDisplayValue.CANCELED, key: BookingStatus.CANCELED },
  { label: BookingStatusDisplayValue.REFUSED, key: BookingStatus.REFUSED },
  { label: BookingStatusDisplayValue.WAITING, key: BookingStatus.WAITING },
];

const TYPE_OPTIONS = [
  { value: BookingTypes.ACTIVITY_BOOKING, label: "Activités" },
  { value: BookingTypes.EVENT_BOOKING, label: "Événements" },
  { value: BookingTypes.PLACE_BOOKING, label: "Établissements" },
  { value: BookingTypes.SPECIAL_OFFER_BOOKING, label: "Offres" },
  { value: BookingTypes.PACK_BOOKING, label: "Packs" },
];

const PAYMENT_OPTIONS = [
  { value: PaymentOptions.IN_PLACE, label: "En place" },
  { value: PaymentOptions.WAFACASH, label: "WafaCash" },
  { value: PaymentOptions.CREDIT_CARD, label: "Carte de crédit" },
  { value: PaymentOptions.STRIPE, label: "Stripe" },
  { value: PaymentOptions.USER_BALANCE, label: "Solde" },
];

interface FormFields {
  search: string;
  place: string;
  SpecialOffers: string;
  type: string;
  paymentMethod: string;
}

interface Props {
  disableSearchInput?: boolean;
  disableFilters?: boolean;
  mode?: "all" | "user";
  userId?: string;
}

export function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className="d-flex flex-column gap-1"
      style={{ minWidth: 170, flex: "1 1 170px", maxWidth: 240 }}
    >
      <label className="text-muted mb-0" style={{ fontSize: 11 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function PillSelect({
  value,
  onChange,
  disabled,
  allLabel = "Tous",
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  allLabel?: string | null;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      className="form-select px-3"
      style={controlStyle}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {allLabel !== null && <option value="">{allLabel}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export default function BookingFilters({
  disableSearchInput = false,
  disableFilters = false,
  mode = "all",
}: Props) {
  const [searchParams] = useSearchParams();
  const { register, watch, setValue } = useForm<FormFields>();
  const { addMultiple, add } = useQueryParam();
  const search = useDebounce(watch("search"), 1000);
  const [selectedPlace, setSelectedPlace] = useState("");
  const [selectedOffer, setSelectedOffer] = useState("");
  const [selectedPartenaire, setSelectedPartenaire] = useState("");
  const [selectedVisiteReference, setSelectedVisiteReference] = useState("");

  const navigate = useNavigate();
  const tab = searchParams.get("tab") || "0";

  const { isAdmin, isMultiPlaceAdmin } = useAuth();

  useEffect(() => {
    setValue("search", searchParams.get("search") as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    addMultiple({ search: search || "", tab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tab]);

  const resetStateFilters = () => {
    setValue("search", "");
    setValue("type", "");
    setSelectedPlace("");
    setSelectedOffer("");
    setSelectedPartenaire("");
    setSelectedVisiteReference("");

    const currentTab = searchParams.get("tab") || "0";
    navigate(`${window.location.pathname}?tab=${currentTab}`);
  };

  return (
    <nav className="d-flex flex-wrap align-items-end gap-3">
      {/* Status */}
      <FilterField label="Statut">
        <PillSelect
          value={searchParams.get("status") || BookingStatus.ALL}
          onChange={(v) => add("status", v)}
          disabled={disableFilters}
          allLabel={null}
          options={STATUS_FILTERS.map((f) => ({
            value: String(f.key),
            label: String(f.label),
          }))}
        />
      </FilterField>

      {isAdmin && (
        <>
          <FilterField label="Type">
            <PillSelect
              value={searchParams.get("type") || ""}
              onChange={(v) => {
                add("type", v);
                setValue("type", v);
              }}
              disabled={disableFilters}
              options={TYPE_OPTIONS}
            />
          </FilterField>

          <FilterField label="Méthode de paiement">
            <PillSelect
              value={searchParams.get("paymentMethod") || ""}
              onChange={(v) => {
                add("paymentMethod", v);
                setValue("paymentMethod", v);
              }}
              disabled={disableFilters}
              options={PAYMENT_OPTIONS}
            />
          </FilterField>

          <FilterField label="Lieu">
            <InfiniteScrollSelect
              variant="pill"
              key={"places"}
              isClearable={false}
              getOptionLabel={(option: Place) => option?.name}
              getOptionValue={(option: Place) => option.id}
              isOptionSelected={(option: Place) =>
                option.id === searchParams.get("place")
              }
              url={{ path: "places" }}
              value={selectedPlace}
              placeholder={selectedPlace.length > 0 ? selectedPlace : "Tous"}
              onChange={(place: Place) => {
                setSelectedPlace(place.name);
                add("place", place?.id);
              }}
            />
          </FilterField>

          <FilterField label="Offre">
            <InfiniteScrollSelect
              variant="pill"
              key={"specials"}
              isClearable={false}
              getOptionLabel={(option: SpecialOffers) => option?.title ?? ""}
              getOptionValue={(option: SpecialOffers) => option.id}
              isOptionSelected={(option: SpecialOffers) =>
                option.id === searchParams.get("specialOffer")
              }
              url={{ path: "specials" }}
              value={selectedOffer}
              placeholder={selectedOffer.length > 0 ? selectedOffer : "Toutes"}
              onChange={(offer: SpecialOffers) => {
                setSelectedOffer(offer.title);
                add("specialOffer", offer?.id);
              }}
            />
          </FilterField>
        </>
      )}

      {isMultiPlaceAdmin && (
        <>
          <FilterField label="Partenaire">
            <InfiniteScrollSelect
              key={"users"}
              isClearable={true}
              getOptionLabel={(option: PlaceManager) => option?.fullName ?? ""}
              getOptionValue={(option: PlaceManager) => option.id}
              isOptionSelected={(option: PlaceManager) =>
                option.id === searchParams.get("partenaire")
              }
              url={{ path: "users/place-manager" }}
              value={selectedPartenaire}
              placeholder={
                selectedPartenaire.length > 0 ? selectedPartenaire : "Tous"
              }
              onChange={(partner: PlaceManager) => {
                setSelectedPartenaire(partner.fullName);
                add("partenaire", partner?.id);
              }}
            />
          </FilterField>

          <FilterField label="Visite référence">
            <InfiniteScrollSelect
              key={"visiteReference"}
              isClearable={true}
              getOptionLabel={(option: PlaceManager) => option?.fullName ?? ""}
              getOptionValue={(option: PlaceManager) => option.id}
              isOptionSelected={(option: PlaceManager) =>
                option.id === searchParams.get("visiteReference")
              }
              url={{ path: "users/place-manager" }}
              value={selectedVisiteReference}
              placeholder={
                selectedVisiteReference.length > 0
                  ? selectedVisiteReference
                  : "Tous"
              }
              onChange={(partner: PlaceManager) => {
                setSelectedVisiteReference(partner.fullName);
                add("visiteReference", partner?.id);
              }}
            />
          </FilterField>
        </>
      )}

      {/* Search */}
      {!disableSearchInput && (
        <FilterField label="Recherche">
          <div
            className="d-flex align-items-center gap-2 px-3"
            style={controlStyle}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              placeholder="Rechercher"
              {...register("search")}
              readOnly={disableFilters}
              disabled={disableFilters}
            />
          </div>
        </FilterField>
      )}

      <button
        type="button"
        onClick={() => {
          resetStateFilters();
          if (mode === "all") navigate("/bookings");
        }}
        className="btn btn-outline-danger rounded-pill px-3 ms-auto"
        style={{ height: 38, fontSize: 13 }}
        disabled={disableFilters}
      >
        Réinitialiser
      </button>
    </nav>
  );
}
